<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import TenantModal from './components/TenantModal.vue';
import UnitModal from './components/UnitModal.vue';
import RoomsModal from './components/RoomsModal.vue';
import LanguagesCard from './components/LanguagesCard.vue';
import { apiErrorCode, apiErrorMessage } from '../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from './constants';
import { hasRooms } from './residentsForm';
import { pageToStepBackTo } from './helpers';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DELAY_MS = 300;

export default {
  name: 'CoLivingResidentsPage',
  components: {
    PageHeader,
    PaginationBar,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    TenantModal,
    UnitModal,
    RoomsModal,
    LanguagesCard,
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      mode: 'tenants',
      search: '',
      condoFilter: this.$route.query.condo_id || '',
      languageFilter: '',
      statusFilter: '',
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      loadError: '',
      tenantModalOpen: false,
      editingTenant: null,
      unitModalOpen: false,
      editingUnit: null,
      roomsUnitId: '',
      toDelete: null,
      isDeleting: false,
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters('coLivingResidents', {
      tenants: 'getTenants',
      tenantMeta: 'getTenantMeta',
      units: 'getUnits',
      unitMeta: 'getUnitMeta',
      uiFlags: 'getUiFlags',
    }),
    ...mapGetters('coLivingCondos', { condos: 'getCondos' }),
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    isTenants() {
      return this.mode === 'tenants';
    },
    rows() {
      return this.isTenants ? this.tenants : this.units;
    },
    total() {
      return (this.isTenants ? this.tenantMeta : this.unitMeta).total;
    },
    isFetching() {
      return this.isTenants
        ? this.uiFlags.isFetchingTenants
        : this.uiFlags.isFetchingUnits;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
    languages() {
      return (this.settings && this.settings.languages) || [];
    },
    roomsUnit() {
      return this.units.find(unit => unit.id === this.roomsUnitId) || null;
    },
    query() {
      const query = { page: this.page, page_size: this.pageSize };
      if (this.search.trim()) query.search = this.search.trim();
      if (this.condoFilter) query.condo_id = this.condoFilter;
      if (this.isTenants) {
        if (this.languageFilter) query.language = this.languageFilter;
        if (this.statusFilter) query.status = this.statusFilter;
      }
      return query;
    },
  },
  mounted() {
    this.fetchCondos();
    this.fetchSettings();
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    ...mapActions('coLivingResidents', [
      'fetchTenants',
      'fetchUnits',
      'removeTenant',
      'removeUnit',
    ]),
    ...mapActions('coLivingCondos', ['fetchCondos']),
    ...mapActions('coLivingConfig', ['fetchSettings']),
    hasRooms,
    async load() {
      this.loadError = '';
      try {
        const data = this.isTenants
          ? await this.fetchTenants(this.query)
          : await this.fetchUnits(this.query);
        const back = pageToStepBackTo(data, this.page, this.pageSize);
        if (back) {
          this.page = back;
          await this.load();
        }
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    setMode(mode) {
      if (this.mode === mode) return;
      this.mode = mode;
      this.page = 1;
      this.search = '';
      this.load();
    },
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, SEARCH_DELAY_MS);
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.onFilterChange();
    },
    openAddTenant() {
      this.editingTenant = null;
      this.tenantModalOpen = true;
    },
    openEditTenant(tenant) {
      this.editingTenant = tenant;
      this.tenantModalOpen = true;
    },
    openAddUnit() {
      this.editingUnit = null;
      this.unitModalOpen = true;
    },
    openEditUnit(unit) {
      this.editingUnit = unit;
      this.unitModalOpen = true;
    },
    askDelete(kind, item) {
      this.toDelete = { kind, item };
    },
    async confirmDelete() {
      const { kind, item } = this.toDelete;
      this.isDeleting = true;
      try {
        if (kind === 'tenant') {
          const result = await this.removeTenant(item.id);
          useAlert(
            this.$t(
              result && result.result === 'archived'
                ? 'CO_LIVING.RESIDENTS.TENANT_ARCHIVED'
                : 'CO_LIVING.RESIDENTS.TENANT_DELETED'
            )
          );
        } else {
          await this.removeUnit(item.id);
          useAlert(this.$t('CO_LIVING.RESIDENTS.UNIT_DELETED'));
        }
        this.load();
      } catch (err) {
        useAlert(
          apiErrorCode(err) === 'in_use'
            ? this.$t('CO_LIVING.RESIDENTS.UNIT_IN_USE')
            : apiErrorMessage(this, err, 'CO_LIVING.RESIDENTS.DELETE_FAILED')
        );
      } finally {
        this.isDeleting = false;
        this.toDelete = null;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-residents-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <p class="mb-4 text-sm text-slate-600 dark:text-slate-300">
      {{ $t('CO_LIVING.RESIDENTS.INTRO') }}
    </p>

    <div class="flex items-center justify-between gap-4 mb-4">
      <div class="flex gap-2" role="group">
        <woot-button
          :color-scheme="isTenants ? 'primary' : 'secondary'"
          data-testid="co-living-mode-tenants"
          @click="setMode('tenants')"
        >
          {{ $t('CO_LIVING.RESIDENTS.MODE_TENANTS') }}
        </woot-button>
        <woot-button
          :color-scheme="!isTenants ? 'primary' : 'secondary'"
          data-testid="co-living-mode-units"
          @click="setMode('units')"
        >
          {{ $t('CO_LIVING.RESIDENTS.MODE_UNITS') }}
        </woot-button>
      </div>
      <woot-button
        v-if="isTenants"
        icon="add"
        data-testid="co-living-add-tenant"
        @click="openAddTenant"
      >
        {{ $t('CO_LIVING.RESIDENTS.ADD_TENANT') }}
      </woot-button>
      <woot-button
        v-else
        icon="add"
        data-testid="co-living-add-unit"
        @click="openAddUnit"
      >
        {{ $t('CO_LIVING.RESIDENTS.ADD_UNIT') }}
      </woot-button>
    </div>

    <div class="flex flex-wrap gap-3 mb-4">
      <input
        v-model="search"
        type="search"
        :placeholder="
          $t(
            isTenants
              ? 'CO_LIVING.RESIDENTS.SEARCH_TENANTS'
              : 'CO_LIVING.RESIDENTS.SEARCH_UNITS'
          )
        "
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-residents-search"
        @input="onSearchInput"
      />
      <select
        v-model="condoFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-residents-condo-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TEAM.ALL_CONDOS') }}</option>
        <option v-for="condo in condos" :key="condo.id" :value="condo.id">
          {{ condo.name }}
        </option>
      </select>
      <template v-if="isTenants">
        <select
          v-model="languageFilter"
          class="max-w-xs"
          :class="[selectClass]"
          data-testid="co-living-residents-language-filter"
          @change="onFilterChange"
        >
          <option value="">
            {{ $t('CO_LIVING.RESIDENTS.ALL_LANGUAGES') }}
          </option>
          <option
            v-for="language in languages"
            :key="language"
            :value="language"
          >
            {{ language }}
          </option>
        </select>
        <select
          v-model="statusFilter"
          class="max-w-xs"
          :class="[selectClass]"
          data-testid="co-living-residents-status-filter"
          @change="onFilterChange"
        >
          <option value="">
            {{ $t('CO_LIVING.RESIDENTS.STATUS.NOT_ARCHIVED') }}
          </option>
          <option value="active">
            {{ $t('CO_LIVING.RESIDENTS.STATUS.ACTIVE') }}
          </option>
          <option value="inactive">
            {{ $t('CO_LIVING.RESIDENTS.STATUS.INACTIVE') }}
          </option>
          <option value="archived">
            {{ $t('CO_LIVING.RESIDENTS.STATUS.ARCHIVED') }}
          </option>
          <option value="all">{{ $t('CO_LIVING.COMMON.ALL_STATUSES') }}</option>
        </select>
      </template>
    </div>

    <LoadingState
      v-if="isFetching && !rows.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>
    <p
      v-else-if="!rows.length"
      class="text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-residents-empty"
    >
      {{
        $t(
          isTenants
            ? 'CO_LIVING.RESIDENTS.EMPTY_TENANTS'
            : 'CO_LIVING.RESIDENTS.EMPTY_UNITS'
        )
      }}
    </p>
    <template v-else>
      <div
        class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <table
          v-if="isTenants"
          class="w-full text-sm"
          data-testid="co-living-tenants-table"
        >
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">
                {{ $t('CO_LIVING.RESIDENTS.COLUMNS.TENANT') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.CONDO') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.UNIT') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.ROOM') }}</th>
              <th class="p-3">
                {{ $t('CO_LIVING.RESIDENTS.COLUMNS.LANGUAGE') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="tenant in tenants"
              :key="tenant.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-tenant-row-${tenant.id}`"
            >
              <td class="p-3">
                {{ tenant.name }}
                <span class="block text-xs text-slate-500">{{
                  tenant.phone
                }}</span>
              </td>
              <td class="p-3">{{ tenant.condo_name }}</td>
              <td class="p-3">{{ tenant.unit_name }}</td>
              <td class="p-3">{{ tenant.room_name || '-' }}</td>
              <td class="p-3">{{ tenant.language || '-' }}</td>
              <td class="p-3">
                {{
                  $t(
                    `CO_LIVING.RESIDENTS.STATUS.${tenant.status.toUpperCase()}`
                  )
                }}
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <woot-button
                  v-if="tenant.status !== 'archived'"
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  icon="edit"
                  :data-testid="`co-living-tenant-edit-${tenant.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  @click="openEditTenant(tenant)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :data-testid="`co-living-tenant-delete-${tenant.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  @click="askDelete('tenant', tenant)"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <table
          v-else
          class="w-full text-sm"
          data-testid="co-living-units-table"
        >
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.UNIT') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.CONDO') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.TYPE') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.RESIDENTS.COLUMNS.ROOMS') }}</th>
              <th class="p-3">
                {{ $t('CO_LIVING.RESIDENTS.COLUMNS.TENANTS') }}
              </th>
              <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="unit in units"
              :key="unit.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
              :data-testid="`co-living-unit-row-${unit.id}`"
            >
              <td class="p-3">{{ unit.name }}</td>
              <td class="p-3">{{ unit.condo_name }}</td>
              <td class="p-3">
                {{ $t(`CO_LIVING.RESIDENTS.KINDS.${unit.kind.toUpperCase()}`) }}
              </td>
              <td class="p-3">
                {{ hasRooms(unit.kind) ? unit.rooms.length : '-' }}
              </td>
              <td class="p-3">{{ unit.tenant_count }}</td>
              <td class="p-3">
                {{
                  unit.active
                    ? $t('CO_LIVING.COMMON.ACTIVE')
                    : $t('CO_LIVING.COMMON.INACTIVE')
                }}
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <woot-button
                  v-if="hasRooms(unit.kind)"
                  variant="clear"
                  size="small"
                  :data-testid="`co-living-unit-rooms-${unit.id}`"
                  @click="roomsUnitId = unit.id"
                >
                  {{ $t('CO_LIVING.RESIDENTS.MANAGE_ROOMS') }}
                </woot-button>
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  icon="edit"
                  :data-testid="`co-living-unit-edit-${unit.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  @click="openEditUnit(unit)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :data-testid="`co-living-unit-delete-${unit.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  @click="askDelete('unit', unit)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationBar
        :pagination="pagination"
        :page-size-options="pageSizeOptions"
        :loading="isFetching"
        @previous="goToPage(page - 1)"
        @next="goToPage(page + 1)"
        @page-size-change="onPageSizeChange"
      />
    </template>

    <div v-if="isTenants" class="mt-6">
      <LanguagesCard />
    </div>

    <TenantModal
      v-if="tenantModalOpen"
      :tenant="editingTenant"
      :preset-condo-id="condoFilter"
      @close="tenantModalOpen = false"
      @saved="load"
    />
    <UnitModal
      v-if="unitModalOpen"
      :unit="editingUnit"
      :preset-condo-id="condoFilter"
      @close="unitModalOpen = false"
      @saved="load"
    />
    <RoomsModal
      v-if="roomsUnit"
      :unit="roomsUnit"
      @close="roomsUnitId = ''"
      @changed="load"
    />
    <DeleteModal
      v-if="toDelete"
      :delete-name="toDelete.item.name"
      :is-loading="isDeleting"
      @close="toDelete = null"
      @confirm="confirmDelete"
    >
      <p class="mt-2 text-sm text-slate-500">
        {{
          $t(
            toDelete.kind === 'tenant'
              ? 'CO_LIVING.RESIDENTS.TENANT_DELETE_NOTE'
              : 'CO_LIVING.RESIDENTS.UNIT_DELETE_NOTE'
          )
        }}
      </p>
    </DeleteModal>
  </div>
</template>
